import React, { useState } from 'react';
import { Modal, Input, Button, Typography, Space, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const { Title, Text } = Typography;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: var(--radius-xl);
    padding: var(--spacing-xl);
  }
  
  .ant-modal-header {
    border-bottom: 1px solid var(--color-border-light);
    padding-bottom: var(--spacing-lg);
    margin-bottom: var(--spacing-xl);
  }
  
  .ant-modal-body {
    padding: 0;
  }
  
  .ant-modal-footer {
    border-top: 1px solid var(--color-border-light);
    padding-top: var(--spacing-lg);
    margin-top: var(--spacing-xl);
  }
`;

const InputContainer = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const StyledInput = styled(Input)`
  height: 48px;
  border-radius: var(--radius-md);
  border: 2px solid var(--color-border-medium);
  font-size: 16px;
  
  &:focus, &:hover {
    border-color: var(--color-primary);
    box-shadow: var(--shadow-primary);
  }
`;

const PriceContainer = styled.div`
  background: var(--gradient-tag);
  padding: var(--spacing-lg);
  border-radius: var(--radius-lg);
  margin-bottom: var(--spacing-lg);
  text-align: center;
`;

const MilesText = styled(Text)`
  font-size: 24px;
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-dark);
`;

const JoinButton = styled(Button)`
  height: 48px;
  border-radius: var(--radius-md);
  background: var(--gradient-primary);
  border: none;
  font-weight: var(--font-weight-semibold);
  font-size: 16px;
  
  &:hover {
    background: var(--color-primary-dark);
    box-shadow: var(--shadow-primary-hover);
  }
  
  &:disabled {
    background: var(--color-gray-300);
    color: var(--color-gray-500);
  }
`;

interface PrivateCampaignJoinModalProps {
  visible: boolean;
  onCancel: () => void;
  onJoin: (code?: string) => void;
  loading: boolean;
  finalFee?: number;
  campaignName: string;
  isPublicCampaign?: boolean;
}

const PrivateCampaignJoinModal: React.FC<PrivateCampaignJoinModalProps> = ({
  visible,
  onCancel,
  onJoin,
  loading,
  finalFee = 0,
  campaignName,
  isPublicCampaign = false
}) => {
  const { t } = useTranslation();
  const [joinCode, setJoinCode] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleJoinClick = () => {
    if (finalFee > 0 && !showConfirmation) {
      setShowConfirmation(true);
    } else {
      if (isPublicCampaign) {
        onJoin(); // No code needed for public campaigns
      } else {
        onJoin(joinCode);
      }
    }
  };

  const handleConfirmJoin = () => {
    setShowConfirmation(false);
    if (isPublicCampaign) {
      onJoin();
    } else {
      onJoin(joinCode);
    }
  };

  const handleCancel = () => {
    setJoinCode('');
    setShowConfirmation(false);
    onCancel();
  };

  const isJoinDisabled = () => {
    if (isPublicCampaign) {
      return loading;
    }
    return !joinCode.trim() || loading;
  };

  // For public campaigns with fee, skip straight to confirmation
  if (isPublicCampaign && finalFee > 0 && !showConfirmation && visible) {
    setTimeout(() => setShowConfirmation(true), 0);
  }

  // Confirmation step for campaigns with fees
  if (showConfirmation && finalFee > 0) {
    return (
      <StyledModal
        title={t('#Campaign.confirm_join')}
        open={visible}
        onCancel={() => setShowConfirmation(false)}
        footer={null}
        centered
        className="PrivateCampaignModal"
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            message={t('#Campaign.miles_deduction_warning')}
            description={t('#Campaign.miles_deduction_description', { miles: finalFee })}
            type="warning"
            showIcon
          />

          <div>
            <Title level={4} style={{ marginBottom: 8 }}>
              {campaignName}
            </Title>
            <Text type="secondary">
              {isPublicCampaign
                ? t('#Campaign.public_campaign_confirmation_message')
                : t('#Campaign.private_campaign_confirmation_message')
              }
            </Text>
          </div>

          <PriceContainer>
            <Text style={{ marginBottom: 8, display: 'block' }}>
              {t('#Campaign.miles_to_be_deducted')}
            </Text>
            <MilesText>{finalFee} {t('common.miles')}</MilesText>
          </PriceContainer>

          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button
              size="large"
              onClick={() => setShowConfirmation(false)}
              style={{
                borderRadius: 'var(--radius-md)',
                minWidth: 120
              }}
            >
              {t('common.cancel')}
            </Button>
            <JoinButton
              type="primary"
              size="large"
              loading={loading}
              onClick={handleConfirmJoin}
              style={{ minWidth: 120 }}
            >
              {t('#Campaign.confirm_join')}
            </JoinButton>
          </Space>
        </Space>
      </StyledModal>
    );
  }

  // For public campaigns without fee, don't show modal at all - handle directly in banner
  if (isPublicCampaign && finalFee === 0) {
    return null;
  }

  // Main modal for private campaigns or initial step
  return (
    <StyledModal
      title={isPublicCampaign ? t('#Campaign.confirm_join') : t('#Campaign.join_private_campaign')}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      centered
      className="PrivateCampaignModal"
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ marginBottom: 8 }}>
            {campaignName}
          </Title>
          <Text type="secondary">
            {isPublicCampaign
              ? t('#Campaign.public_campaign_join_message')
              : t('#Campaign.enter_join_code_description')
            }
          </Text>
        </div>

        {!isPublicCampaign && (
          <InputContainer>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>
              {t('#Campaign.join_code')}
            </Text>
            <StyledInput
              placeholder={t('#Campaign.join_code_placeholder')}
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value)}
              maxLength={50}
            />
          </InputContainer>
        )}

        {finalFee > 0 && (
          <PriceContainer>
            <Text style={{ marginBottom: 8, display: 'block' }}>
              {t('#Campaign.campaign_fee')}
            </Text>
            <MilesText>{finalFee} {t('common.miles')}</MilesText>
          </PriceContainer>
        )}

        <JoinButton
          type="primary"
          size="large"
          block
          loading={loading}
          disabled={isJoinDisabled()}
          onClick={handleJoinClick}
        >
          {finalFee > 0
            ? t('#Campaign.join_with_miles', { miles: finalFee })
            : (isPublicCampaign ? t('#Campaign.join_now') : t('#Campaign.join_now'))
          }
        </JoinButton>
      </Space>
    </StyledModal>
  );
};

export default PrivateCampaignJoinModal;